import recaptcha from '@components/App/RecaptchaWidget/recaptcha'
import { LoaderActions } from '@store/loaders'
import type { LoaderType } from '@store/loaders/types'
import useModalExport from '@storeOld/hooks/useModalExport'
import axios from 'axios'
import qs from 'qs'
import { useDispatch } from 'react-redux'

import { DEFAULT_PROJECT_NAME } from '@/constants/constants'
import { useArtboardMode } from '@/hooks'
import type { UseControlsReducerState, UseSubmit } from './types'

const initialState: UseControlsReducerState = {
  isLogoAndTitleEnabled: true,
  isColorLegendEnabled: true,
  isStickyNotesEnabled: true,
}

const ExportPdfApiUrl: string | undefined = import.meta.env
  .VITE_EXPORT_PDF_API_URL

const ExportAppHost: string | undefined = import.meta.env.VITE_EXPORT_HOST

export const usePdf = (
  loader: LoaderType,
  opt = initialState,
  onDone?: () => void,
  onError?: () => void,
  exportUrl?: string
): UseSubmit => {
  const dispatch = useDispatch()
  const { projectName, projectId, tabId } = useModalExport()
  const processedExportUrl = exportUrl?.startsWith('/')
    ? exportUrl?.slice(1)
    : exportUrl
  const fileName = projectName || DEFAULT_PROJECT_NAME

  const { gallery: images, estimate } = useArtboardMode()

  const getExportAppHost = () => {
    const { host } = window.location
    return host.includes('localhost') ? ExportAppHost : host
  }

  const submit = async () => {
    dispatch(LoaderActions.set(loader))
    const recaptchaResponseKey = await recaptcha.execute({
      action: 'export_as_pdf',
    })

    const search = qs.stringify(
      {
        tabId,
        print: true,
        notes: opt.isStickyNotesEnabled,
        legend: opt.isColorLegendEnabled,
        header: opt.isLogoAndTitleEnabled,
        filename: fileName,
        pdf: true,
        images,
        estimate,
        responseKey: recaptchaResponseKey,
      },
      { encode: true }
    )

    const url = exportUrl
      ? `${ExportPdfApiUrl}/pdf/${getExportAppHost()}/${processedExportUrl}?${search}`
      : `${ExportPdfApiUrl}/pdf/${getExportAppHost()}/${projectId}?${search}`

    await axios({
      url,
      method: 'GET',
      responseType: 'blob',
    })
      .then((response) => {
        const url = window.URL.createObjectURL(
          new Blob([response.data], { type: 'application/pdf' })
        )
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `${fileName}.pdf`)
        document.body.appendChild(link)
        link.click()
        onDone?.()
      })
      .catch(() => {
        onError?.()
      })
      .finally(() => {
        dispatch(LoaderActions.unset(loader))
      })
  }

  return { submit }
}
